import React, { useState } from "react";
import ShowLearningFeedback from "../components/ShowLearningFeedback";

const GameScreen = ({
  theme,
  setTheme,
  isGameOver,
  gameType,
  gameLength,
  feedback,
  playMode,
  isPaused,
  goHome,
  lives,
  currentIndex,
  words,
  score,
  elapsedTime,
  currentWord,
  handleConnotation,
  handleVisualization,
  handleOptionClick,
  handleLearnContinue,
  options,
  selectedOption,
  showLivesOverPopup,
  confirmLivesOver,
  isSettingsOpen,
  handleOpenSettings,
  handleCloseSettings,
  showMeaningFeedback,
  toggleMeaningFeedback,
  muted,
  toggleSoundMute,
  user,
  speedyCount,
  isShowingMeaning,
  correctOptionId,
  selectedPair,
  correctPairIds
}) => {
  const [touchStartX, setTouchStartX] = useState(null);
  const [touchEndX, setTouchEndX] = useState(null);

  if (isGameOver) return null;

  const isDark = theme === "dark";
  const isTwins = gameType === "twins";
  const isToneClash = gameType === "tone_clash";

  const handleTouchStart = (e) => {
    setTouchStartX(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEndX(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStartX || !touchEndX) return;

    const distance = touchStartX - touchEndX;
    const threshold = 50;

    if (distance > threshold) {
      handleConnotation("negative"); // LEFT
    } else if (distance < -threshold) {
      handleConnotation("positive"); // RIGHT
    }

    setTouchStartX(null);
    setTouchEndX(null);
  };

  const swipeOffset = (touchStartX && touchEndX) ? touchEndX - touchStartX : 0;
  const leftOpacity = swipeOffset < -20 ? Math.min(1, 0.7 + Math.abs(swipeOffset) / 150) : 0.7;
  const rightOpacity = swipeOffset > 20 ? Math.min(1, 0.7 + Math.abs(swipeOffset) / 150) : 0.7;

  // Physics for draggable feel
  const rotation = swipeOffset / 20;
  const translateX = swipeOffset;
  const cardTransform = gameType === "connotation" && !feedback
    ? `translate3d(${translateX}px, 0, 0) rotate(${rotation}deg)`
    : "translate3d(0,0,0) rotate(0deg)";

  const isMeaningMatch = gameType === "meaning" || gameType === "twins" || gameType === "tone_clash";

  // Dynamic Theme Colors
  const colors = {
    background: isDark ? "#0F172A" : "#F8FBFA",
    surface: isDark ? "#1E293B" : "#FFFFFF",
    text: isDark ? "#F1F5F9" : "#1E293B",
    secondaryText: isDark ? "#94A3B8" : "#64748B",
    border: isDark ? "rgba(16, 185, 129, 0.12)" : "rgba(16, 185, 129, 0.08)",
    shell: isDark ? "#334155" : "#EAF7F6"
  };

  return (
    <div style={{
      position: "fixed",
      top: 0, left: 0,
      height: "100dvh",
      width: "100%",
      overflowY: "auto",
      overflowX: "hidden",
      boxSizing: "border-box",
      overscrollBehavior: "none",
      background: colors.background,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      padding: isMeaningMatch
        ? `calc(25px + env(safe-area-inset-top)) 20px calc(20px + env(safe-area-inset-bottom)) 20px`
        : (gameType === "visualization"
          ? `calc(min(3vh, 20px) + env(safe-area-inset-top)) 16px calc(min(2vh, 10px) + env(safe-area-inset-bottom)) 16px`
          : "20px 20px"),
      gap: isMeaningMatch ? "16px" : (gameType === "visualization" ? "min(1.5vh, 12px)" : "20px"),
      justifyContent: (gameType === "visualization" || isMeaningMatch) ? "space-between" : "flex-start",
      fontFamily: "'Inter', sans-serif"
    }}>


      {/* Modern Swipe Indicators (Overlays) - Pinned to Root edges */}
      {gameType === "connotation" && (
        <>
          <div style={{
            position: "absolute",
            left: 0, top: 0, bottom: 0,
            width: "20%",
            background: isDark ? "linear-gradient(to right, rgba(239, 68, 68, 0.1), transparent)" : "linear-gradient(to right, rgba(239, 68, 68, 0.18), transparent)",
            display: "flex", alignItems: "center", justifyContent: "flex-start",
            paddingLeft: "12px", pointerEvents: "none", zIndex: 5,
            opacity: leftOpacity, transition: "opacity 0.1s ease"
          }}>
            <div style={{ color: "#EF4444", fontSize: "32px", fontWeight: "800" }}>←</div>
          </div>
          <div style={{
            position: "absolute",
            right: 0, top: 0, bottom: 0,
            width: "20%",
            background: isDark ? "linear-gradient(to left, rgba(34, 197, 94, 0.1), transparent)" : "linear-gradient(to left, rgba(34, 197, 94, 0.18), transparent)",
            display: "flex", alignItems: "center", justifyContent: "flex-end",
            paddingRight: "12px", pointerEvents: "none", zIndex: 5,
            opacity: rightOpacity, transition: "opacity 0.1s ease"
          }}>
            <div style={{ color: "#22C55E", fontSize: "32px", fontWeight: "800" }}>→</div>
          </div>
        </>
      )}

      {/* LIVES OVER MODAL */}
      {showLivesOverPopup && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: isDark ? "rgba(0, 0, 0, 0.7)" : "rgba(15, 23, 42, 0.4)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 1000, padding: "20px", backdropFilter: "blur(4px)"
        }}>
          <div style={{
            background: colors.surface,
            borderRadius: "32px",
            padding: "40px 32px",
            width: "100%",
            maxWidth: "360px",
            boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
            border: `1px solid ${isDark ? '#334155' : '#F1F5F9'}`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            gap: "24px",
            animation: "popupFadeIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)"
          }}>
            <div style={{ width: "100px", height: "100px", background: isDark ? "rgba(255, 241, 242, 0.1)" : "#FFF1F2", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <img src="/images/game_s/gameover.webp" alt="Broken Heart" style={{ width: "70px", height: "70px", objectFit: "contain" }} onError={(e) => e.target.style.display = 'none'} />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <h2 style={{ fontSize: "28px", fontWeight: "800", color: colors.text, margin: 0, letterSpacing: "-0.5px" }}>Out of Lives</h2>
              <p style={{ fontSize: "16px", fontWeight: "600", color: colors.secondaryText, margin: 0, lineHeight: "1.5" }}>
                Nice run! Review your results to see what you missed and improve.
              </p>
            </div>

            <button
              onClick={confirmLivesOver}
              style={{
                width: "100%",
                background: "#10B981",
                color: "white",
                border: "none",
                borderRadius: "20px",
                padding: "18px 24px",
                fontSize: "18px",
                fontWeight: "700",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "12px",
                cursor: "pointer",
                boxShadow: "0 8px 16px rgba(16, 185, 129, 0.2)",
                transition: "all 0.2s ease"
              }}
            >
              Review Results
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
            </button>
          </div>
        </div>
      )}

      {/* HEADER HUD */}
      <div style={{
        width: "100%",
        maxWidth: "480px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 10px",
        zIndex: 100
      }}>
        {/* PAUSE BUTTON */}
        <button
          onClick={handleOpenSettings}
          style={{
            background: colors.surface,
            border: "none",
            width: gameType === "visualization" ? "48px" : "60px",
            height: gameType === "visualization" ? "48px" : "60px",
            borderRadius: gameType === "visualization" ? "14px" : "18px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 4px 15px rgba(0,0,0,0.03)",
            cursor: "pointer",
            color: "#00A99D"
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <rect x="7" y="5" width="3" height="14" rx="1.5" />
            <rect x="14" y="5" width="3" height="14" rx="1.5" />
          </svg>
        </button>

        {/* SCORE DISPLAY */}
        <div style={{
          background: colors.surface,
          padding: gameType === "visualization" ? "4px 14px" : "8px 24px",
          borderRadius: "20px",
          boxShadow: isDark ? "0 8px 24px rgba(0,0,0,0.2)" : "0 8px 24px rgba(0,0,0,0.03)",
          display: "flex",
          alignItems: "center",
          gap: gameType === "visualization" ? "8px" : "15px",
          minWidth: gameType === "visualization" ? "120px" : "160px",
          border: isDark ? "1px solid #334155" : "1px solid rgba(16, 185, 129, 0.05)"
        }}>
          <svg width="34" height="34" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ transform: (gameType === "visualization" || gameType === "connotation") ? "scale(0.85)" : "none" }}>
            <path d="M6 9H4.5C3.67 9 3 9.67 3 10.5V12C3 13.66 4.34 15 6 15" stroke="#F59E0B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M18 9H19.5C20.33 9 21 9.67 21 10.5V12C21 13.66 19.66 15 18 15" stroke="#F59E0B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M6 5V12C6 15.31 8.69 18 12 18C15.31 18 18 15.31 18 12V5H6Z" fill="#FFFBEB" stroke="#F59E0B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M9 22H15M12 18V22" stroke="#F59E0B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
            <span style={{ fontSize: "11px", fontWeight: "700", color: colors.secondaryText, textTransform: "uppercase", letterSpacing: "1px" }}>Score</span>
            <span style={{ fontSize: gameType === "visualization" ? "28px" : "32px", fontWeight: "900", color: "#00A99D", lineHeight: 1 }}>{score}</span>
          </div>
        </div>

        {/* SPEEDY STAT */}
        <div style={{
          background: colors.surface,
          width: gameType === "visualization" ? "48px" : "60px",
          height: gameType === "visualization" ? "48px" : "60px",
          borderRadius: gameType === "visualization" ? "14px" : "18px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: isDark ? "0 8px 24px rgba(0,0,0,0.2)" : "0 8px 24px rgba(0,0,0,0.03)",
          border: isDark ? "1px solid #334155" : "1px solid rgba(16, 185, 129, 0.05)"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ transform: (gameType === "visualization" || gameType === "connotation") ? "scale(0.9)" : "none" }}>
              <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" fill="#10B981" stroke="#10B981" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span style={{ fontSize: gameType === "visualization" ? "16px" : "20px", fontWeight: "900", color: "#00A99D" }}>{speedyCount}</span>
          </div>
          <span style={{ fontSize: gameType === "visualization" ? "8px" : "9px", fontWeight: "800", color: colors.secondaryText, textTransform: "uppercase" }}>Speedy</span>
        </div>
      </div>

      {/* PROGRESS BAR */}
      <div style={{ width: "100%", maxWidth: "420px", marginTop: (isMeaningMatch || gameType === "visualization") ? "2px" : "10px", flexShrink: 0 }}>
        <div style={{ width: "100%", height: "8px", background: isDark ? "#334155" : "#E2E8F0", borderRadius: "4px", overflow: "hidden" }}>
          <div style={{
            width: `${((currentIndex + 1) / words.length) * 100}%`,
            height: "100%",
            background: "#00A99D",
            transition: "width 0.3s ease"
          }} />
        </div>
        <div style={{ textAlign: "center", marginTop: (isMeaningMatch || gameType === "visualization") ? "2px" : "12px", fontSize: "13px", fontWeight: "700", color: colors.secondaryText, letterSpacing: "0.5px" }}>
          {currentIndex + 1} / {words.length} WORDS
        </div>
      </div>

      {/* LIVES AND TIMER ROW */}
      <div style={{
        width: "100%",
        maxWidth: "420px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "0 5px"
      }}>
        <div style={{ display: "flex", gap: "6px" }}>
          {Array.from({ length: gameLength === "quick" ? 3 : 5 }).map((_, i) => (
            <span key={i} style={{ fontSize: gameType === "visualization" ? "20px" : "24px", filter: i >= lives ? "grayscale(100%) opacity(0.3)" : "none", transition: "all 0.3s ease" }}>
              ❤️
            </span>
          ))}
        </div>
        <div style={{ fontSize: gameType === "visualization" ? "20px" : "24px", fontWeight: "800", color: "#00A99D", fontVariantNumeric: "tabular-nums" }}>
          {(() => {
            const m = Math.floor(elapsedTime / 60);
            const s = elapsedTime % 60;
            return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
          })()}
        </div>
      </div>

      {/* MAIN PLAY AREA */}
      <div style={{
        width: "100%",
        maxWidth: "420px",
        display: "flex",
        flexDirection: "column",
        gap: isToneClash ? "8px" : (isMeaningMatch ? "16px" : (gameType === "visualization" ? "min(2vh, 16px)" : "20px")),
        flex: 1,
        justifyContent: (isMeaningMatch || gameType === "visualization") ? "space-evenly" : "flex-start",
        paddingBottom: (isMeaningMatch || gameType === "visualization") ? "10px" : "0px",
        minHeight: 0
      }}>

        {/* MAIN WORD CARD */}
        {!isTwins && (
          <div style={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            transform: cardTransform,
            transition: (touchStartX && touchEndX) ? "none" : "transform 0.2s ease",
          }}>
            {isMeaningMatch ? (
              /* LAYERED FRAME FOR MEANING MATCH */
              <div style={{
                width: "100%",
                background: feedback === "correct" ? (isDark ? "#064E3B" : "#DCFCE7") : feedback === "wrong" ? (isDark ? "#7F1D1D" : "#FEE2E2") : colors.shell,
                borderRadius: "44px",
                padding: "5px",
                boxShadow: isDark ? "0 15px 50px rgba(0, 0, 0, 0.3)" : "0 12px 40px rgba(0, 0, 0, 0.04)",
                transition: "background 0.2s ease"
              }}>
                <div
                  style={{
                    background: feedback === "correct" ? (isDark ? "rgba(6, 78, 59, 0.3)" : "#F0FDF4") : feedback === "wrong" ? (isDark ? "rgba(127, 29, 29, 0.3)" : "#FEF2F2") : colors.surface,
                    borderRadius: "40px",
                    padding: isToneClash ? "24px 24px" : "40px 24px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: isToneClash ? "140px" : "200px",
                    position: "relative",
                    transition: "background 0.2s ease",
                  }}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                  <h1 style={{
                    fontSize: currentWord?.word?.length > 10 ? "38px" : "50px",
                    fontWeight: "900",
                    color: colors.text,
                    margin: 0,
                    textAlign: "center",
                    letterSpacing: "-1px"
                  }}>
                    {currentWord?.word}
                  </h1>

                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    margin: "20px 0",
                    gap: "10px"
                  }}>
                    <div style={{ flex: 1, height: "1.5px", background: "linear-gradient(to right, transparent, #00A99D)" }} />
                    <div style={{ width: "8px", height: "8px", background: "#00A99D", transform: "rotate(45deg)" }} />
                    <div style={{ flex: 1, height: "1.5px", background: "linear-gradient(to left, transparent, #00A99D)" }} />
                  </div>

                  <p style={{ fontSize: "15px", fontWeight: "700", color: colors.secondaryText, margin: 0 }}>
                    {isToneClash ? "Which word clashes?" : (isTwins ? "Choose the synonym" : "Choose the meaning")}
                  </p>
                </div>
              </div>
            ) : gameType === "visualization" ? (
              /* COMPACT CARD FOR VISUALIZATION */
              <div
                style={{
                  width: "100%",
                  background: feedback === "correct" ? (isDark ? "rgba(6, 78, 59, 0.3)" : "#F0FDF4") : feedback === "wrong" ? (isDark ? "rgba(127, 29, 29, 0.3)" : "#FEF2F2") : colors.surface,
                  borderRadius: "32px",
                  padding: "min(3vh, 32px) 24px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: isDark ? "0 10px 30px rgba(0, 0, 0, 0.2)" : "0 8px 30px rgba(0, 169, 157, 0.04)",
                  border: feedback
                    ? (feedback === "correct" ? "3px solid #10B981" : "3px solid #EF4444")
                    : `1px solid ${colors.border}`,
                  flex: "0 1 auto",
                  minHeight: "min(18vh, 140px)",
                  position: "relative",
                  transition: "all 0.2s ease",
                }}
              >
                <h1 style={{
                  fontSize: currentWord?.word?.length > 10 ? "clamp(28px, 4vh, 34px)" : "clamp(34px, 6vh, 48px)",
                  fontWeight: "900",
                  color: colors.text,
                  margin: 0,
                  textAlign: "center",
                  letterSpacing: "-1px"
                }}>
                  {currentWord?.word}
                </h1>

                <div style={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  margin: "min(2vh, 20px) 0",
                  gap: "10px"
                }}>
                  <div style={{ flex: 1, height: "1.5px", background: "linear-gradient(to right, transparent, #00A99D)" }} />
                  <div style={{ width: "8px", height: "8px", background: "#00A99D", transform: "rotate(45deg)" }} />
                  <div style={{ flex: 1, height: "1.5px", background: "linear-gradient(to left, transparent, #00A99D)" }} />
                </div>

                <p style={{ fontSize: "clamp(12px, 2vh, 15px)", fontWeight: "700", color: colors.secondaryText, margin: 0 }}>Choose the corresponding image</p>
              </div>
            ) : (
              /* ORIGINAL CARD FOR OTHER MODES */
              <div
                style={{
                  width: gameType === "connotation" ? "86%" : "100%",
                  maxWidth: gameType === "connotation" ? "360px" : "100%",
                  background: feedback === "correct" ? (isDark ? "rgba(6, 78, 59, 0.3)" : "#F0FDF4") : feedback === "wrong" ? (isDark ? "rgba(127, 29, 29, 0.3)" : "#FEF2F2") : colors.surface,
                  borderRadius: gameType === "connotation" ? "32px" : "40px",
                  padding: gameType === "connotation" ? "40px 24px" : "60px 40px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: isDark ? "0 15px 50px rgba(0, 0, 0, 0.3)" : "0 10px 40px rgba(0, 169, 157, 0.05)",
                  border: feedback
                    ? (feedback === "correct" ? "3px solid #10B981" : "3px solid #EF4444")
                    : `1px solid ${colors.border}`,
                  aspectRatio: gameType === "connotation" ? "1 / 1" : "auto",
                  minHeight: gameType === "connotation" ? "0" : "260px",
                  position: "relative",
                  transition: "background 0.2s ease, border 0.2s ease, transform 0.2s ease",
                }}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                <h1 style={{
                  fontSize: currentWord?.word?.length > 10 ? (gameType === "connotation" ? "42px" : "42px") : (gameType === "connotation" ? "52px" : "54px"),
                  fontWeight: "900",
                  color: colors.text,
                  margin: 0,
                  textAlign: "center",
                  letterSpacing: gameType === "connotation" ? "-1.5px" : "-1px",
                  transform: gameType === "connotation" ? "translateY(-10px)" : "none" // Fine-tune centering
                }}>
                  {currentWord?.word}
                </h1>

                {gameType === "connotation" ? (
                  <div style={{ position: "absolute", bottom: "25px", left: 0, right: 0, display: "flex", flexDirection: "column", alignItems: "center", pointerEvents: "none" }}>
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      margin: "0 0 12px",
                      gap: "10px",
                      opacity: 0.4
                    }}>
                      <div style={{ flex: 1, height: "1.5px", background: "linear-gradient(to right, transparent, #00A99D)" }} />
                      <div style={{ width: "6px", height: "6px", background: "#00A99D", transform: "rotate(45deg)" }} />
                      <div style={{ flex: 1, height: "1.5px", background: "linear-gradient(to left, transparent, #00A99D)" }} />
                    </div>

                    <p style={{
                      fontSize: "13px",
                      fontWeight: "600",
                      color: colors.secondaryText,
                      margin: 0,
                      opacity: 0.6,
                      letterSpacing: "0.3px"
                    }}>
                      Swipe toward the right connotation
                    </p>
                  </div>
                ) : (
                  <>
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      margin: "30px 0",
                      gap: "10px"
                    }}>
                      <div style={{ flex: 1, height: "1.5px", background: "linear-gradient(to right, transparent, #00A99D)" }} />
                      <div style={{ width: "8px", height: "8px", background: "#00A99D", transform: "rotate(45deg)" }} />
                      <div style={{ flex: 1, height: "1.5px", background: "linear-gradient(to left, transparent, #00A99D)" }} />
                    </div>

                    <p style={{ fontSize: "16px", fontWeight: "700", color: colors.secondaryText, margin: 0 }}>
                      {gameType === "twins" ? "Choose the synonym" : "Choose the meaning"}
                    </p>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {isTwins && (
          <div style={{ textAlign: 'center', marginBottom: '10px' }}>
            <h1 style={{ color: colors.text, fontSize: '32px', fontWeight: '900', margin: '0 0 8px 0' }}>Match the Twins</h1>
            <p style={{ color: colors.secondaryText, fontWeight: '600', margin: 0 }}>Tap the two synonyms</p>
          </div>
        )}

        {/* OPTIONS */}
        <div style={{
          display: isTwins ? "grid" : "flex",
          gridTemplateColumns: isTwins ? "1fr 1fr" : "none",
          flexDirection: isTwins ? "none" : "column",
          gap: isTwins ? "16px" : (isToneClash ? "8px" : (isMeaningMatch ? "10px" : "14px")),
          width: "100%",
          paddingBottom: "10px"
        }}>
          {isMeaningMatch && currentWord && options.map((opt, i) => {
            let status = null;
            if (isTwins) {
              const isSelected = selectedPair.some(s => s.id === opt.id);
              if (feedback === "correct" && correctPairIds.includes(opt.id)) status = "correct";
              else if (feedback === "wrong") {
                if (correctPairIds.includes(opt.id)) status = "correct";
                else if (isSelected) status = "wrong";
              } else if (isSelected) {
                status = "selected";
              }
            } else {
              if (feedback === "correct" && opt.id === correctOptionId) status = "correct";
              if (feedback === "wrong") {
                if (opt.id === correctOptionId) status = "correct";
                else if (opt.id === selectedOption?.id) status = "wrong";
              }
            }

            return (
              <button
                key={i}
                onClick={() => handleOptionClick(opt)}
                style={{
                  background: status === "correct" ? "#10B981" : status === "wrong" ? "#EF4444" : (status === "selected" ? (isDark ? "#334155" : "#DBEAFE") : colors.surface),
                  border: status === "selected" ? "2px solid #3B82F6" : (isDark ? (status ? "none" : "1px solid #334155") : (status ? "none" : "1px solid rgba(16, 185, 129, 0.08)")),
                  borderRadius: isTwins ? "32px" : "24px",
                  padding: isTwins ? "30px 20px" : (isToneClash ? "12px 20px" : "16px 24px"),
                  display: "flex",
                  flexDirection: isTwins ? "column" : "row",
                  alignItems: "center",
                  gap: isTwins ? "12px" : "16px",
                  width: "100%",
                  minHeight: isTwins ? "140px" : "auto",
                  cursor: "pointer",
                  boxShadow: isDark ? "0 6px 20px rgba(0,0,0,0.15)" : "0 4px 20px rgba(0,0,0,0.03)",
                  transition: "all 0.2s ease",
                  textAlign: isTwins ? "center" : "left",
                  justifyContent: isTwins ? "center" : "flex-start"
                }}
              >
                {!isTwins && (
                  <div style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    background: status ? "rgba(255,255,255,0.2)" : (isDark ? "rgba(255,255,255,0.05)" : "#EAF7F6"),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "18px",
                    fontWeight: "800",
                    color: status ? "white" : "#00A99D",
                    flexShrink: 0
                  }}>
                    {i + 1}
                  </div>
                )}
                <span style={{
                  fontSize: isTwins ? "20px" : "16px",
                  fontWeight: "800",
                  color: status === "correct" || status === "wrong" ? "white" : (status === "selected" && !isDark ? "#1E40AF" : colors.text),
                  lineHeight: "1.3"
                }}>
                  {(isTwins || isToneClash) ? opt.word : opt.meaning}
                </span>
              </button>
            );
          })}

          {/* Visualization Image Grid */}
          {gameType === "visualization" && currentWord && (
            <div style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "min(1.5vh, 10px)",
              width: "100%",
              maxWidth: "340px",
              margin: "min(1vh, 8px) auto 0",
              flexShrink: 0
            }}>
              {options.map((imageUrl, i) => (
                <img key={i} src={imageUrl} alt={`Option ${i + 1}`}
                  style={{
                    width: "100%",
                    aspectRatio: "1 / 1",
                    objectFit: "cover",
                    borderRadius: "20px",
                    cursor: "pointer",
                    border: (feedback === "wrong" && imageUrl === currentWord.imageUrl)
                      ? "4px solid #10B981"
                      : (imageUrl === selectedOption && feedback === "wrong")
                        ? "4px solid #EF4444"
                        : (imageUrl === selectedOption && feedback === "correct")
                          ? "4px solid #10B981"
                          : `2px solid ${isDark ? '#334155' : 'transparent'}`,
                    boxShadow: "0 4px 15px rgba(0,0,0,0.05)"
                  }}
                  onClick={() => handleVisualization(imageUrl)} />
              ))}
            </div>
          )}

          {/* Connotation Buttons */}
          {gameType === "connotation" && (
            <div style={{ display: "flex", gap: "16px", width: "100%" }}>
              <button
                onClick={() => handleConnotation("negative")}
                style={{
                  flex: 1,
                  background: isDark ? "#450A0A" : "#FEF2F2",
                  border: isDark ? "1.5px solid #991B1B" : "1.5px solid #FCA5A5",
                  borderRadius: "24px",
                  padding: "18px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "10px",
                  fontSize: "16px",
                  fontWeight: "800",
                  color: "#EF4444",
                  cursor: "pointer",
                  boxShadow: "0 6px 15px rgba(239, 68, 68, 0.1)"
                }}
              >
                ☹️ Negative
              </button>
              <button
                onClick={() => handleConnotation("positive")}
                style={{
                  flex: 1,
                  background: isDark ? "#064E3B" : "#F0FDF4",
                  border: isDark ? "1.5px solid #065F46" : "1.5px solid #86EFAC",
                  borderRadius: "24px",
                  padding: "18px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "10px",
                  fontSize: "16px",
                  fontWeight: "800",
                  color: "#22C55E",
                  cursor: "pointer",
                  boxShadow: "0 6px 15px rgba(34, 197, 94, 0.1)"
                }}
              >
                😊 Positive
              </button>
            </div>
          )}
        </div>
      </div>

      {/* PAUSE MODAL */}
      {isSettingsOpen && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: isDark ? "rgba(0, 0, 0, 0.8)" : "rgba(15, 23, 42, 0.6)", backdropFilter: "blur(8px)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 2100, padding: "20px"
        }}>
          <div style={{
            background: colors.surface, borderRadius: "32px", padding: "32px",
            width: "100%", maxWidth: "360px", boxShadow: "0 25px 50px rgba(0,0,0,0.3)",
            display: "flex", flexDirection: "column", gap: "24px",
            border: isDark ? "1px solid #334155" : "none",
            animation: "modalSlideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)"
          }}>
            <div style={{ textAlign: "center" }}>
              <h2 style={{ fontSize: "28px", fontWeight: "900", color: colors.text, margin: 0, letterSpacing: "-0.5px" }}>Paused</h2>
              <p style={{ fontSize: "14px", color: colors.secondaryText, fontWeight: "500", marginTop: "4px" }}>Take a breather, {user.username}</p>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {/* THEME TOGGLE */}
              <div style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                background: isDark ? "#0F172A" : "#F8FAFC", padding: "16px", borderRadius: "20px", border: `1px solid ${isDark ? '#334155' : '#F1F5F9'}`
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <div style={{ width: "40px", height: "40px", background: colors.surface, borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 6px rgba(0,0,0,0.03)" }}>
                    <span style={{ fontSize: "20px" }}>{isDark ? "🌙" : "☀️"}</span>
                  </div>
                  <span style={{ fontWeight: "700", color: colors.text }}>Theme</span>
                </div>
                <button
                  onClick={() => {
                    const next = isDark ? "light" : "dark";
                    setTheme(next);
                    localStorage.setItem("app_theme", next);
                  }}
                  style={{
                    width: "54px", height: "30px", borderRadius: "99px",
                    background: isDark ? "#10B981" : "#E2E8F0",
                    border: "none", position: "relative", cursor: "pointer", transition: "all 0.3s ease"
                  }}
                >
                  <div style={{
                    width: "24px", height: "24px", background: "white", borderRadius: "50%",
                    position: "absolute", top: "3px", left: isDark ? "27px" : "3px", transition: "all 0.3s ease",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                  }} />
                </button>
              </div>

              {/* SOUNDS TOGGLE */}
              <div style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                background: isDark ? "#0F172A" : "#F8FAFC", padding: "16px", borderRadius: "20px", border: `1px solid ${isDark ? '#334155' : '#F1F5F9'}`
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <div style={{ width: "40px", height: "40px", background: colors.surface, borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 6px rgba(0,0,0,0.03)" }}>
                    <span style={{ fontSize: "20px" }}>{muted ? "🔇" : "🔊"}</span>
                  </div>
                  <span style={{ fontWeight: "700", color: colors.text }}>Sounds</span>
                </div>
                <button
                  onClick={toggleSoundMute}
                  style={{
                    width: "54px", height: "30px", borderRadius: "99px",
                    background: muted ? "#E2E8F0" : "#10B981",
                    border: "none", position: "relative", cursor: "pointer", transition: "all 0.3s ease"
                  }}
                >
                  <div style={{
                    width: "24px", height: "24px", background: "white", borderRadius: "50%",
                    position: "absolute", top: "3px", left: muted ? "3px" : "27px", transition: "all 0.3s ease",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                  }} />
                </button>
              </div>

              {/* MEANINGS TOGGLE */}
              <div style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                background: isDark ? "#0F172A" : "#F8FAFC", padding: "16px", borderRadius: "20px", border: `1px solid ${isDark ? '#334155' : '#F1F5F9'}`
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <div style={{ width: "40px", height: "40px", background: colors.surface, borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 6px rgba(0,0,0,0.03)" }}>
                    <span style={{ fontSize: "20px" }}>💡</span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span style={{ fontWeight: "700", color: colors.text, fontSize: "14px" }}>Meanings</span>
                    <span style={{ fontSize: "11px", color: colors.secondaryText }}>After wrong answers</span>
                  </div>
                </div>
                <button
                  onClick={toggleMeaningFeedback}
                  style={{
                    width: "54px", height: "30px", borderRadius: "99px",
                    background: !showMeaningFeedback ? "#E2E8F0" : "#10B981",
                    border: "none", position: "relative", cursor: "pointer", transition: "all 0.3s ease"
                  }}
                >
                  <div style={{
                    width: "24px", height: "24px", background: "white", borderRadius: "50%",
                    position: "absolute", top: "3px", left: !showMeaningFeedback ? "3px" : "27px", transition: "all 0.3s ease",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                  }} />
                </button>
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginTop: "8px" }}>
              <button
                onClick={handleCloseSettings}
                style={{
                  width: "100%", background: "#10B981", color: "white", border: "none",
                  borderRadius: "18px", padding: "18px", fontSize: "18px", fontWeight: "800",
                  cursor: "pointer", boxShadow: "0 8px 20px rgba(16, 185, 129, 0.2)"
                }}
              >
                Resume Game
              </button>
              <button
                onClick={goHome}
                style={{
                  width: "100%", background: isDark ? "#334155" : "#F1F5F9", color: isDark ? "#CBD5E1" : "#64748B", border: "none",
                  borderRadius: "18px", padding: "16px", fontSize: "16px", fontWeight: "700",
                  cursor: "pointer"
                }}
              >
                Exit Game
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Learning Feedback Modal */}
      {isShowingMeaning && currentWord && (
        <ShowLearningFeedback
          word={gameType === "tone_clash" ? currentWord : currentWord.word}
          meaning={currentWord.meaning}
          onContinue={handleLearnContinue}
          theme={theme}
        />
      )}
    </div>
  );
};

export default GameScreen;
